import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import { Header } from "../../components/shared/Header";
import { Input } from "../../components/shared/inputs/Input";
import { NoData } from "../../components/shared/NoData";
import { Pagination } from "../../components/shared/Pagination";
import { getClassWeekPaymentSlipUrl, getFormatedDate, getFormatedTime, validatePDF } from "../../helpers/Common";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { Button } from "../../components/shared/Button";
import { useAlert } from "../../hooks/useAlert";
import { OnlineClassWeek } from "../../models/OnlineClassWeek";
import { OnlineClassSearchCriteria } from "../../models/OnlineClassSearchCriteria";
import { getClassWeeks, saveClassWeekChanges } from "../../services/OnlineClassService";
import { Switch } from "../../components/shared/inputs/Switch";
import PDFViewer from "../../components/shared/PDFViewer";
import Dropzone from "react-dropzone";
import { ReactComponent as FileUpload } from "../../assets/svgs/file-upload.svg";
import { ReactComponent as CloseCircle } from "../../assets/svgs/close-circle-ash.svg";
import { TableLoader } from "../../components/loaders/TableLoader";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";

export const OnlineClassWeeksPage: React.FC = () => {
    const [searchParams] = useSearchParams(); 
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("onlineClassWeeksPage"));  
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const classId: number | undefined = searchParams.get("class-id") === null ? undefined : +searchParams.get("class-id")!;
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const [totalPages, setTotalPages] = useState<number>();       
    const pageSize: number = 50;
    const [searchTeacherName, setSearchTeacherName] = useState<string>("");
    const [teacherName, setTeacherName] = useState<string>();        
    const [searchWhatsappNo, setSearchWhatsappNo] = useState<string>("");
    const [whatsappNo, setWhatsappNo] = useState<string>();    
    const [searchTitle, setSearchTitle] = useState<string>("");
    const [title, setTitle] = useState<string>();
    const [date, setDate] = useState<string>();
    const [classWeeks, setClassWeeks] = useState<OnlineClassWeek[]>([]);
    const [selectedClassWeek, setSelectedClassWeek] = useState<OnlineClassWeek>();
    const [slipFile, setSlipFile] = useState<File>();
    const [slipFileUrl, setSlipFileUrl] = useState<string>();
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" }); 
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();   
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [openFileDialog, setOpenFileDialog] = useState<boolean>(false);
    const {Alert, openAlert} = useAlert();
    const [loading, setLoading] = useState<boolean>(true); 
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
    }, []);

    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("onlineClassWeeksPage")); 
    }, [isMobile]);

    useEffect(() => {
        getWeekList();                                    
    }, [page, teacherName, whatsappNo, title, date]);

    async function getWeekList() {        
        setLoading(true);
        let searchModel: OnlineClassSearchCriteria = {
            systemAdminId: admin!.adminId,
            pageNo: page,
            recordsPerPage: pageSize,    
            classId: classId,            
            teacherName: teacherName,            
            whatsappNo: whatsappNo,	 
            title: title,       
	        date: date ? date.split('T')[0] : undefined
        }
        await getClassWeeks(searchModel, admin!.token)
        .then((weekList: OnlineClassWeek[]) => {
            setClassWeeks(weekList);            
            if(weekList.length > 0) { 
                let totalPageCount = Math.ceil(weekList[0].recordCount / pageSize);            
                setTotalPages(totalPageCount);                
            }  
            else {
                setTotalPages(undefined);
            }  
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const handlePagination = (pageNumber: number) => {
        searchParams.set("page", pageNumber.toString());                     
        const newUrl = `?${searchParams}`;
        navigate(newUrl);        
    }

    const handleTeacherNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchTeacherName(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setTeacherName(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleWhatsappNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchWhatsappNo(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setWhatsappNo(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchTitle(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setTitle(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setDate(value);
    };

    const selectClassWeek = (classWeek: OnlineClassWeek) => {
        setSelectedClassWeek(classWeek);
        if(classWeek.isRevenueTransferred) {
            const slipURL: string = getClassWeekPaymentSlipUrl(classWeek.onlineClassWeekId, admin!.adminId, admin!.token);
            setSlipFileUrl(slipURL);                
        }
        setOpenDialog(true);
    };

    const handleIsCompletedChange = () => {        
        setSelectedClassWeek((values: any) => ({ ...values, ["isCompleted"]: !selectedClassWeek!.isCompleted }));        
    };

    const handleIsRevenueTransferredChange = () => {        
        setSelectedClassWeek((values: any) => ({ ...values, ["isRevenueTransferred"]: !selectedClassWeek!.isRevenueTransferred }));         
    };

    const handleSlipFileChange = (uploadedFiles: File[]) => { 
        let fileUrl: string;
        if(uploadedFiles.length > 0) {
            const uploadedSlip = uploadedFiles[0];
            const slipValidation = validatePDF(uploadedSlip);
            if(slipValidation.valid) {
                setSlipFile(uploadedSlip);
                fileUrl = URL.createObjectURL(uploadedSlip);
                setSlipFileUrl(fileUrl);                                                                                                   
            }
            else{
                openAlert("error", slipValidation.invalidMessage!);
            }                                           
        }
        // Revoke the object URL on component unmount
        return () => URL.revokeObjectURL(fileUrl);
    }
    
    const removeSelectedSlipFile = () => {        
        setSlipFile(undefined);
        setSlipFileUrl(undefined);        
    }

    async function handleSubmitChanges() {        
        const formData = new FormData();
        const modelString = JSON.stringify(selectedClassWeek);
        formData.append('modelString', modelString);  
        if(slipFile)      
            formData.append('file', slipFile);
        closeDialog();
        await saveClassWeekChanges(formData, admin!.adminId, admin!.token)
        .then((success: boolean) => {              
            if(success) {
                getWeekList();
                openAlert("success", "Class week changes has been submitted successfully.");                                                                    
            }
            else {
                openAlert("error", "Oops! something went wrong.");
            }
        })
        .catch((error) => {            
            toErrorPage(error);
        }); 
    }

    const closeDialog = () => {
        setSelectedClassWeek(undefined);
        setSlipFile(undefined);
        setSlipFileUrl(undefined);
        setOpenDialog(false);
    }

    return (   
        <>
            <div style={styles}>
                <Header />          
                <h1 style={styles.heading}>{"Online Classes - Weeks"}</h1>      
                <div style={styles.searchWrapper}>
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>
                            {"Teacher Name"}
                        </label>
                        <Input
                            type={"text"}
                            value={searchTeacherName}
                            styles={styles.input}
                            onChange={handleTeacherNameChange}                                                                                    
                        />
                    </div>
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>
                            {"Whatsapp Number"}
                        </label>
                        <Input
                            type={"text"}
                            value={searchWhatsappNo}
                            styles={styles.input}
                            onChange={handleWhatsappNoChange}                                                                                    
                        />
                    </div>
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>
                            {"Week Title"}
                        </label>
                        <Input
                            type={"text"}
                            value={searchTitle}
                            styles={styles.input}
                            onChange={handleTitleChange}                                                                                    
                        />
                    </div>                    
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>
                            {"Date"}
                        </label>
                        <Input
                            type={"date"}
                            value={date}
                            styles={styles.input}
                            onChange={handleDateChange}                                                                                    
                        />
                    </div>
                </div>
                {loading ? <TableLoader columnCount={6} rowCount={10} /> :
                <div>                
                    {(classWeeks.length === 0) ? <NoData/> :
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.table.header}> 
                                <th style={styles.table.idColumn}>{"Id"}</th>                           
                                <th style={styles.table.teacherColumn}>{"Teacher"}</th>
                                <th style={styles.table.weekColumn}>{"Week"}</th> 
                                <th style={styles.table.paymentColumn}>{"Payment"}</th>
                                <th style={styles.table.studentColumn}>{"Students"}</th>
                                <th style={styles.table.revenueColumn}>{"Revenue"}</th>
                                <th style={styles.table.transferColumn}>{"Transferred"}</th>                                
                            </tr>
                        </thead>
                        <tbody>
                            {classWeeks.map((classWeek, index) => (
                                <tr 
                                    key={index} 
                                    style={{...styles.table.tr, 
                                        ...(index === hoveredIndex && styles.table.hoveredTr), 
                                        ...(((classWeek.isExpired || classWeek.isCompleted) && classWeek.isRevenueTransferred) && styles.table.expiredTr),
                                        ...(((classWeek.isExpired || classWeek.isCompleted) && !classWeek.isRevenueTransferred) && styles.table.transferTr)}}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(-1)}
                                    onClick={() => selectClassWeek(classWeek)}
                                >
                                    <td>{classWeek.onlineClassWeekId}</td>
                                    <td>
                                        {classWeek.teacherName}<br/>
                                        {classWeek.whatsappNo}
                                    </td>
                                    <td>
                                        {classWeek.title}<br/>
                                        {getFormatedDate(classWeek.date)} {getFormatedTime(classWeek.time)}
                                    </td>
                                    <td>
                                        {classWeek.isMonthlyPayment ? "Monthly Payment" : "Weekly Payment"}<br/>
                                        {classWeek.classFee + " LKR"}                                        
                                    </td>
                                    <td>
                                        {classWeek.paidStudentCount + " Paid Students"}<br/>
                                        {classWeek.attendStudentCount + " Attend Students"}
                                    </td>
                                    <td>
                                        {"Gross Revenue: " + classWeek.weekGrossRevenue}<br/>
                                        {"Net Revenue: " + (classWeek.weekGrossRevenue - (classWeek.weekGrossRevenue * 0.04)) + " LKR"}
                                    </td>
                                    <td>{classWeek.isRevenueTransferred ? "Revenue Transferred" : "Not Transferred"}</td>                                    
                                </tr>
                            ))}
                        </tbody>
                    </table>}
                    {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination}/>}
                </div>}
            </div>                 
            <PopUpDialog styles={styles.dialog} open={openDialog} toggle={closeDialog}>
                {selectedClassWeek && <>
                    <div style={styles.dialog.rowWrapper}>
                        <div style={styles.dialog.idColumn}>{selectedClassWeek.onlineClassWeekId}</div>
                        <div style={styles.dialog.teacherColumn}>
                            {selectedClassWeek.teacherName}<br/>
                            {selectedClassWeek.whatsappNo}
                        </div>
                        <div style={styles.dialog.weekColumn}>
                            {selectedClassWeek.title}<br/>
                            {getFormatedDate(selectedClassWeek.date)} {getFormatedTime(selectedClassWeek.time)}
                        </div>
                        <div style={styles.dialog.paymentColumn}>
                            {selectedClassWeek.isMonthlyPayment ? "Monthly Payment" : "Weekly Payment"}<br/>
                            {selectedClassWeek.classFee + " LKR"}                                        
                        </div>
                        <div style={styles.dialog.studentColumn}>
                            {selectedClassWeek.paidStudentCount + " Paid Students"}<br/>
                            {selectedClassWeek.attendStudentCount + " Attend Students"}
                        </div>
                        <div style={styles.dialog.revenueColumn}>
                            {"Gross Revenue: " + selectedClassWeek.weekGrossRevenue}<br/>
                            {"Net Revenue: " + (selectedClassWeek.weekGrossRevenue - (selectedClassWeek.weekGrossRevenue * 0.04)) + " LKR"}
                        </div>
                        <div style={styles.dialog.transferColumn}>{selectedClassWeek.isRevenueTransferred ? "Revenue Transferred" : "Not Transferred"}</div> 
                        <CloseCircle style={styles.dialog.dialogClose} onClick={closeDialog} />                       
                    </div>
                    <div style={styles.dialog.actionWrapper}>
                        <div>
                            <div style={styles.dialog.switchWrapper} onClick={handleIsCompletedChange}>
                                <span style={styles.dialog.switchLabel}>{"Make class as completed:"}</span>
                                <Switch checked={selectedClassWeek.isCompleted}/>                
                            </div>
                            <div style={styles.dialog.switchWrapper} onClick={handleIsRevenueTransferredChange}>
                                <span style={styles.dialog.switchLabel}>{"Make class as revenue transferred:"}</span>
                                <Switch checked={selectedClassWeek.isRevenueTransferred}/>                
                            </div>
                            <div style={styles.dialog.bankWrapper}>
                                <div><span style={styles.dialog.bankLabel}>Bank Name: </span>{selectedClassWeek.bankName}</div>
                                <div><span style={styles.dialog.bankLabel}>Branch Name: </span>{selectedClassWeek.branchName}</div>
                                <div><span style={styles.dialog.bankLabel}>Account Number: </span>{selectedClassWeek.accountNumber}</div>
                                <div><span style={styles.dialog.bankLabel}>Account Holder Name: </span>{selectedClassWeek.accountHolderName}</div>
                            </div>
                        </div> 
                        <div>
                            <div style={styles.dialog.fileLabel}>
                                {"Upload revenue transfer slip (Supported Files: pdf)"}
                            </div>
                            <div style={styles.dialog.rowWrapper}>
                                {slipFileUrl && <>
                                    <PDFViewer pdfUrl={slipFileUrl} styles={styles.dialog.pdfViewer} handleClick={() => setOpenFileDialog(!openFileDialog)}/>
                                    <CloseCircle style={styles.dialog.fileClose} onClick={removeSelectedSlipFile} />
                                </>}
                                <div style={{...styles.dialog.fullWrapper, ...(slipFileUrl && styles.dialog.halfWrapper)}}>
                                    <Dropzone onDrop={acceptedFiles => handleSlipFileChange(acceptedFiles)} multiple={false}>
                                        {({ getRootProps, getInputProps }) => (
                                            <div {...getRootProps({ style: styles.dialog.uploadFile })} className="center-content">
                                                <input {...getInputProps()} /> 
                                                <div style={styles.dialog.uploadIconWrapper}>
                                                    <FileUpload style={styles.dialog.uploadIcon} />
                                                </div>
                                                <div style={styles.dialog.uploadText}>
                                                    {"Add the slip file if available."}                                  
                                                </div>
                                            </div>
                                        )}
                                    </Dropzone>
                                </div>
                            </div>
                        </div>
                    </div> 
                    <Button style={styles.dialog.submitButton} onClick={handleSubmitChanges}>{"Submit Changes"}</Button>                  
                </>}
            </PopUpDialog>
            <PopUpDialog styles={styles.fileDialog} open={openFileDialog} toggle={() => setOpenFileDialog(!openFileDialog)}>
                {slipFileUrl && <>
                    <PDFViewer pdfUrl={slipFileUrl} styles={styles.fileDialog.pdfViewer} />
                    <CloseCircle style={styles.fileDialog.fileClose} onClick={() => setOpenFileDialog(!openFileDialog)} />
                </>}
            </PopUpDialog>
            <Alert />
        </>         
    )
}